import type { NavigationBadges } from '@/services/navigation.service';

/**
 * Which count belongs on which leaf nav row.
 *
 * Extracted from Sidebar.tsx so it can be tested: the leaf row is rendered
 * inline inside <Sidebar>, whose module graph pulls in the auth store, the
 * branding query, the global-facility store and patient search, so nothing
 * left in that closure is reachable from a unit test.
 */

/** Permission 1 = QA visibility. Mirrors NavigationController::main(). */
const QA_VISIBILITY_PERMISSION = 1;

/**
 * Counts that do not come from the badges endpoint. Tasks is deliberately not
 * here: it is a group, so the Sidebar resolves its count as a parent badge.
 */
export interface LeafBadgeExtras {
  /** Legacy fallback for messages when the badges endpoint has not answered. */
  messagesFallback?: number;
  /** The user's unread alert count (useUnreadAlertCount). */
  alerts?: number;
}

export function leafBadgeFor(
  href: string | undefined,
  badges: Partial<NavigationBadges> | undefined,
  userPermissions: number[],
  extras: LeafBadgeExtras = {},
): number | undefined {
  switch (href) {
    case '/messages':
      return badges?.messages ?? extras.messagesFallback;
    case '/qa':
      return userPermissions.includes(QA_VISIBILITY_PERMISSION) ? badges?.qa : undefined;
    case '/alerts':
      return extras.alerts;
    case '/lab-orders':
      return badges?.labs;
    case '/marketing/google-business':
      return badges?.googleReviews;
    default:
      return undefined;
  }
}

/** The Mentions child's href. Shared so the nav config and this map agree. */
export const MENTIONS_NAV_HREF = '/messages?view=mentions';

/** The Communication Center parent's href: the overview page. Shared like MENTIONS_NAV_HREF. */
export const COMM_CENTER_NAV_HREF = '/messages/overview';

export interface GroupBadgeSources {
  badges: Partial<NavigationBadges> | undefined;
  /** Dashboard Inbox unread, from the inbox-counts query. */
  inboxUnread: number | undefined;
  /** Legacy fallback for messages when the badges endpoint has not answered. */
  legacyMessages: number | undefined;
  permissions: number[];
  /**
   * The user's tasks due now: past due + due today. Upcoming tasks are left
   * out on purpose, so this can be lower than the Dashboard's Pending Tasks.
   */
  tasksDueNow?: number;
}

export interface GroupBadges {
  parent: number | undefined;
  /** Keyed by child href. A child with nothing to show has no entry. */
  children: Record<string, number>;
}

/**
 * Which count belongs on an expandable group's parent row and on each child.
 *
 * Dashboard and CRM are the mapping Sidebar held inline before this existed:
 * the parent takes its count as-is (NavBadge hides 0 and undefined) and the one
 * child that owns that number mirrors it only when it is non-zero.
 *
 * Communication Center's parent and Unread child both show the unread-messages
 * count, with the same legacy fallback as the old leaf row. The Mentions child
 * shows unread mentioned conversations, which never feed the parent.
 *
 * Tasks shows the user's tasks due now on the parent only.
 */
export function groupBadgesFor(href: string | undefined, sources: GroupBadgeSources): GroupBadges {
  const { badges, inboxUnread, legacyMessages, permissions, tasksDueNow } = sources;
  const children: Record<string, number> = {};

  switch (href) {
    case '/':
      if (inboxUnread) children['/inbox'] = inboxUnread;
      return { parent: inboxUnread, children };
    case '/crm':
      if (badges?.leads) children['/leads'] = badges.leads;
      return { parent: badges?.leads, children };
    case COMM_CENTER_NAV_HREF: {
      const messages = leafBadgeFor('/messages', badges, permissions, { messagesFallback: legacyMessages });
      if (messages) children['/messages'] = messages;
      if (badges?.mentions) children[MENTIONS_NAV_HREF] = badges.mentions;
      return { parent: messages, children };
    }
    case '/tasks':
      return { parent: tasksDueNow, children };
    default:
      return { parent: undefined, children };
  }
}
